import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { AlertCircle, Mail, ArrowLeft } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import PasswordStrengthMeter from './components/PasswordStrengthMeter';
import PasswordVisibilityToggle from './components/PasswordVisibilityToggle';
import PasswordMatchValidator from './components/PasswordMatchValidator';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import SecurityFeatures from './components/SecurityFeatures';
import SuccessConfirmation from './components/SuccessConfirmation';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [showPasswordMatch, setShowPasswordMatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [linkExpired, setLinkExpired] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    setError,
    clearErrors,
    getValues
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
      confirmPassword: ''
    }
  });

  const watchedNewPassword = watch('newPassword');
  const watchedConfirmPassword = watch('confirmPassword');

  // Extract email and token from URL params or state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');

    if (token && email) {
      setResetToken(token);
      setUserEmail(email);
      // Validate token (simulate API call)
      validateResetToken(token);
    } else {
      // If no token, redirect to forgot password
      navigate('/auth/forgot-password');
    }
  }, [location, navigate]);

  const validateResetToken = async (token) => {
    try {
      // Simulate API call to validate token
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For demo purposes, simulate expired token
      if (token === 'expired-token') {
        setLinkExpired(true);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setNetworkError(true);
    }
  };

  const handlePasswordUpdate = async (data) => {
    setIsLoading(true);
    clearErrors();
    setNetworkError(false);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // For demo purposes, simulate different scenarios
      if (data.newPassword === 'network-error') {
        throw new Error('Network error');
      }

      if (data.newPassword === 'invalid-token') {
        setLinkExpired(true);
        setIsLoading(false);
        return;
      }

      // Success case
      setIsSuccess(true);

    } catch (error) {
      console.error('Password update error:', error);
      setNetworkError(true);
      setError('newPassword', {
        type: 'manual',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionTimeout = () => {
    navigate('/auth/forgot-password', {
      state: {
        message: 'Your session has expired. Please request a new password reset link.'
      }
    });
  };

  const handleSuccessRedirect = () => {
    navigate('/login', {
      state: {
        message: 'Password updated successfully! Please log in with your new password.'
      }
    });
  };

  const handleRequestNewLink = () => {
    navigate('/auth/forgot-password', {
      state: {
        email: userEmail,
        message: 'Your reset link has expired. Please request a new one.'
      }
    });
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const getPasswordStrength = () => {
    const criteria = [
      watchedNewPassword.length >= 8,
      /[A-Z]/.test(watchedNewPassword),
      /[a-z]/.test(watchedNewPassword),
      /\d/.test(watchedNewPassword),
      /[!@#$%^&*(),.?":{}|<>]/.test(watchedNewPassword)
    ];
    return criteria.filter(Boolean).length;
  };

  const isFormValid = () => {
    return (
      watchedNewPassword &&
      watchedConfirmPassword &&
      watchedNewPassword === watchedConfirmPassword &&
      getPasswordStrength() >= 4 &&
      !errors.newPassword &&
      !errors.confirmPassword
    );
  };

  // Success screen
  if (isSuccess) {
    return <SuccessConfirmation onRedirect={handleSuccessRedirect} />;
  }

  // Expired link screen
  if (linkExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-error-50 to-warning-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-soft p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-error-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-accent-800 mb-2">
                Reset Link Expired
              </h1>
              <p className="text-accent-600 text-sm">
                Your password reset link has expired for security reasons. Please request a new one.
              </p>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleRequestNewLink}
                variant="primary"
                size="lg"
                fullWidth
              >
                Request New Reset Link
              </Button>

              <Button
                onClick={handleBackToLogin}
                variant="ghost"
                size="lg"
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Session Timeout Warning */}
        <SessionTimeoutWarning onTimeout={handleSessionTimeout} />

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/Giftsy-Logo-Final-1751671984839.png"
              alt="Giftsy Modern"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl font-heading font-bold text-accent-800 mb-2">
            Reset Your Password
          </h1>
          <p className="text-accent-600 text-sm max-w-sm mx-auto">
            Create a new secure password for your account. Make sure it's strong and unique.
          </p>
        </div>

        {/* User Email Confirmation */}
        {userEmail && (
          <div className="mb-6 p-3 bg-primary-50 border border-primary-200 rounded-lg">
            <div className="flex items-center text-primary-700">
              <Mail size={16} className="mr-2" />
              <span className="text-sm font-medium">Resetting password for:</span>
            </div>
            <p className="text-sm text-primary-600 mt-1 font-medium">
              {userEmail}
            </p>
          </div>
        )}

        {/* Network Error Alert */}
        {networkError && (
          <div className="mb-6 p-3 bg-error-50 border border-error-200 rounded-lg">
            <div className="flex items-center text-error-700">
              <AlertCircle size={16} className="mr-2" />
              <span className="text-sm font-medium">Connection Error</span>
            </div>
            <p className="text-sm text-error-600 mt-1">
              Unable to update password. Please check your internet connection and try again.
            </p>
          </div>
        )}

        {/* Reset Password Form Card */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <form onSubmit={handleSubmit(handlePasswordUpdate)} className="space-y-6">
            {/* New Password Input */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  {...register('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    },
                    validate: {
                      strength: (value) => {
                        const strength = [
                          value.length >= 8,
                          /[A-Z]/.test(value),
                          /[a-z]/.test(value),
                          /\d/.test(value),
                          /[!@#$%^&*(),.?":{}|<>]/.test(value)
                        ].filter(Boolean).length;
                        return strength >= 4 || 'Password is too weak';
                      }
                    }
                  })}
                  placeholder="Enter your new password"
                  className={`pr-12 ${errors.newPassword ? 'border-error' : ''}`}
                  autoComplete="new-password"
                  onFocus={() => setShowPasswordStrength(true)}
                  onBlur={() => setShowPasswordStrength(false)}
                />
                <PasswordVisibilityToggle
                  isVisible={showPassword}
                  onToggle={() => setShowPassword(!showPassword)}
                />
              </div>
              {errors.newPassword && (
                <p className="text-error text-sm mt-1">{errors.newPassword.message}</p>
              )}

              {/* Password Strength Meter */}
              <PasswordStrengthMeter
                password={watchedNewPassword}
                isVisible={showPasswordStrength || watchedNewPassword.length > 0}
              />
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm your new password',
                    validate: {
                      match: (value) => {
                        const newPassword = getValues('newPassword');
                        return value === newPassword || 'Passwords do not match';
                      }
                    }
                  })}
                  placeholder="Confirm your new password"
                  className={`pr-12 ${errors.confirmPassword ? 'border-error' : ''}`}
                  autoComplete="new-password"
                  onFocus={() => setShowPasswordMatch(true)}
                  onBlur={() => setShowPasswordMatch(false)}
                />
                <PasswordVisibilityToggle
                  isVisible={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-error text-sm mt-1">{errors.confirmPassword.message}</p>
              )}

              {/* Password Match Validator */}
              <PasswordMatchValidator
                password={watchedNewPassword}
                confirmPassword={watchedConfirmPassword}
                showValidation={showPasswordMatch || watchedConfirmPassword.length > 0}
              />
            </div>

            {/* Update Password Button */}
            <Button
              type="submit"
              disabled={!isFormValid() || isLoading}
              loading={isLoading}
              fullWidth
              variant="primary"
              size="lg"
              className="mt-6"
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </Button>
          </form>

          {/* Security Features */}
          <SecurityFeatures />

          {/* Back to Login */}
          <div className="text-center mt-6">
            <button
              onClick={handleBackToLogin}
              className="text-accent-600 hover:text-primary-600 text-sm font-medium transition-colors inline-flex items-center"
            >
              <ArrowLeft size={16} className="mr-1" />
              Back to Login
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center text-xs text-accent-500 max-w-sm mx-auto">
          <p>
            Having trouble? Your reset link is valid for 24 hours. If you need help,{' '}
            <a href="/contact" className="text-primary hover:text-primary-600 font-medium">
              contact our support team
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;